'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSearch } from '@/components/data-table/data-table-search';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { getPaymentsTableColumns } from './payments-table-columns';
import { PaymentsTableActionBar } from './payments-table-action-bar';
import { PaymentsTableSkeleton } from './PaymentsTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { Payment } from '@tagadapay/crm-entities';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';

interface PaymentsTableProps {
  payments: Payment.Payment[];
  total: number;
  pageCount: number;
  options: {
    status: string[];
    currency: string[];
    initiatedBy: string[];
    reasonType: string[];
    requireAction: string[];
    processorName: string[];
    paymentMethod: string[];
  };
  isLoading: boolean;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onTableStateChange: (state: MainTableState) => void;
  onRefetch: () => void;
  onViewDetails: (payment: Payment.Payment) => void;
  onRefund: (payment: Payment.Payment) => void;
  /** Resolves to the ids of the payments that were not refunded. */
  onBulkRefund?: (paymentIds: string[], requestId: string) => Promise<string[]>;
  onExportPayments?: () => void;
  isRefunding?: boolean;
  config?: TableConfig;
}

/**
 * PaymentsTable component with comprehensive filtering, sorting, and pagination
 * Uses useDataTable hook for URL synchronization
 */
export function PaymentsTable({
  payments,
  total,
  pageCount,
  options,
  isLoading,
  searchQuery = '',
  onSearchChange,
  onTableStateChange,
  onRefetch,
  onViewDetails,
  onRefund,
  onBulkRefund,
  onExportPayments,
  isRefunding = false,
  config = {},
}: PaymentsTableProps) {
  const { t, i18n } = useTranslation('payments');
  const navigate = useNavigate();
  const { orgRoutes } = useOrgNavigate();

  const handleRowClick = React.useCallback(
    (payment: Payment.Payment) => {
      navigate(orgRoutes.PAYMENT_DETAILS(payment.id));
    },
    [navigate, orgRoutes],
  );

  // Memoize columns but recreate when options change
  // Use a simple count as dependency to ensure React detects changes
  const optionsCount = options.status.length + options.currency.length + options.processorName.length;

  const columns = React.useMemo(
    () =>
      getPaymentsTableColumns({
        onViewDetails,
        onRefund,
        options,
        t,
      }),
    [onViewDetails, onRefund, options, t, i18n.language],
  );

  // Track if options have ever loaded (only changes once)
  const hasLoadedOptionsRef = React.useRef(false);
  if (!hasLoadedOptionsRef.current && optionsCount > 0) {
    hasLoadedOptionsRef.current = true;
  }
  const tableKey = `${hasLoadedOptionsRef.current ? 'loaded' : 'loading'}-${i18n.language}`;

  // Use the useDataTable hook with URL synchronization
  const { table } = useDataTable({
    data: payments,
    columns,
    total,
    config: {
      ...config,
      pageSize: config.pageSize || 10,
      defaultSorting: config.defaultSorting || [{ id: 'createdAt', desc: true }],
    },
    onTableStateChange,
    getRowId: (row: Payment.Payment) => row.id,
  });

  return (
    <DataTable
      key={tableKey}
      table={table}
      actionBar={
        <PaymentsTableActionBar
          table={table}
          onExport={onExportPayments}
          onBulkRefund={onBulkRefund}
          isRefunding={isRefunding}
        />
      }
      isLoading={isLoading}
      skeletonRows={<PaymentsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onExport={onExportPayments}
      onRowClick={handleRowClick}
      extraFilters={<>
        {onSearchChange && (
          <div className='w-full md:w-[200px]'>
            <DataTableSearch value={searchQuery} onChange={onSearchChange} placeholder="Search anything..." />
          </div>
        )}
      </>}
    >
    </DataTable>
  );
}
